import { useEffect } from 'react'

import _ from 'lodash'

import { AuthBrandingCompact } from '@/features/auth/layout/AuthBrandingCompact'
import { Button } from '@/shared/ui'
import { closeDialog } from '@/shared/ui/dialog'
import { AppIcon } from '@/shared/ui/icons'
import type { DigitalProduct } from '@/types/digitalProduct'

const STEPS = [
  {
    icon: 'link' as const,
    text: 'Peça ao organizador o link de convite ou confirme se o ingresso foi emitido.'
  },
  {
    icon: 'envelope' as const,
    text: 'Entre com o mesmo e-mail usado na compra ou no convite.'
  },
  {
    icon: 'arrows-rotate' as const,
    text: 'Quando estiver tudo certo, toque em verificar acesso novamente.'
  }
]

type AuthNoTicketViewProps = {
  digitalProduct?: DigitalProduct
  isChecking?: boolean
  onRetryAccess: () => void
  onUseAnotherAccount: () => void
  onSignOut: () => void
}

/**
 * Participante autenticado sem ingresso/convite para a gamificação do evento.
 * Tom acolhedor no topo — sem parecer tela de erro técnico.
 */
export function AuthNoTicketView({
  digitalProduct,
  isChecking = false,
  onRetryAccess,
  onUseAnotherAccount,
  onSignOut
}: AuthNoTicketViewProps) {
  const eventName =
    _.get(digitalProduct, 'name') ||
    _.get(digitalProduct, 'fantasy_name') ||
    'deste evento'

  // Descarta alertdialog genérico "Oops... NoTicket" se algum fluxo o
  // enfileirou antes de montar esta tela de ciência.
  useEffect(() => {
    closeDialog(false)
  }, [])

  return (
    <section
      aria-labelledby="auth-no-ticket-title"
      className="flex w-full flex-col gap-4"
    >
      <AuthBrandingCompact digitalProduct={digitalProduct} />

      <div className="overflow-hidden rounded-2xl border border-brand/15 bg-gradient-to-b from-brand-light/80 to-white shadow-sm">
        <div className="flex flex-col gap-4 px-4 py-5 sm:px-5">
          <div className="flex flex-col items-center gap-3 text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-2xs font-bold uppercase tracking-wide text-brand shadow-sm">
              <AppIcon name="ticket" size={14} aria-hidden />
              Quase lá
            </span>

            <div className="flex flex-col gap-2">
              <h2
                id="auth-no-ticket-title"
                className="text-lg font-extrabold leading-snug text-gray-900"
              >
                Falta liberar seu acesso
              </h2>
              <p className="text-sm font-semibold leading-relaxed text-gray-700">
                Você entrou com sucesso, mas sua conta ainda não está liberada
                para a gamificação de{' '}
                <span className="text-gray-900">{eventName}</span>. Isso é
                normal quando o convite ou ingresso ainda não foi vinculado.
              </p>
            </div>
          </div>

          <ol className="m-0 flex list-none flex-col gap-2 p-0">
            {STEPS.map((step, index) => (
              <li
                key={step.icon}
                className="flex items-start gap-3 rounded-xl border border-white/80 bg-white/70 px-3 py-2.5 text-left"
              >
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                  <AppIcon name={step.icon} size={14} aria-hidden />
                </span>
                <span className="pt-0.5 text-xs font-semibold leading-snug text-gray-700">
                  <span className="mr-1 font-extrabold text-brand">
                    {index + 1}.
                  </span>
                  {step.text}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className="flex w-full flex-col gap-2.5">
        <Button
          type="button"
          variant="primary"
          className="w-full"
          showTrailingIcon={false}
          disabled={isChecking}
          aria-busy={isChecking || undefined}
          onClick={onRetryAccess}
          leadingIcon={
            isChecking ? (
              <AppIcon
                name="spinner-third"
                size={16}
                className="animate-spin"
                aria-hidden
              />
            ) : (
              <AppIcon name="arrows-rotate" size={16} aria-hidden />
            )
          }
        >
          {isChecking ? 'Verificando acesso…' : 'Verificar acesso novamente'}
        </Button>

        <Button
          type="button"
          variant="light"
          className="w-full"
          showTrailingIcon={false}
          disabled={isChecking}
          onClick={onUseAnotherAccount}
          leadingIcon={<AppIcon name="user" size={16} aria-hidden />}
        >
          Entrar com outra conta
        </Button>

        <Button
          type="button"
          variant="link"
          className="!h-auto min-h-11 w-full p-0 text-sm font-semibold"
          showTrailingIcon={false}
          disabled={isChecking}
          onClick={onSignOut}
        >
          Sair desta conta
        </Button>
      </div>

      <p className="m-0 text-center text-2xs font-semibold leading-relaxed text-gray-600">
        Precisa de ajuda? Escreva para{' '}
        <a
          href="mailto:suporte@evnttz.com.br"
          className="text-brand underline-offset-2 hover:underline"
        >
          suporte@evnttz.com.br
        </a>
        .
      </p>
    </section>
  )
}
